import React, { useEffect, useRef, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import config from '../../config';
import {
    checkFullPartNoExists,
    getCurrentUserCompany,
    MASTER_PART_NO_NOT_FOUND_MESSAGE,
    MASTER_PART_NO_OBSOLETE_MESSAGE,
    MASTER_PART_NO_VALIDATION_ERROR_MESSAGE,
    MASTER_PART_NO_VALIDATION_STATUS,
    normalizeMasterPartNo,
} from '../utils/masterPartNoValidation';

function MasterPartNoInput({
    value,
    onChange,
    onValidationChange,
    onPartDataMatch,
    enabled = true,
    company,
    formControlProps = {},
}) {
    const [status, setStatus] = useState(MASTER_PART_NO_VALIDATION_STATUS.IDLE);
    const [message, setMessage] = useState('');
    const validationId = useRef(0);
    const statusId = useRef(`part-no-validation-${Math.random().toString(36).slice(2)}`);
    const onChangeRef = useRef(onChange);
    const onValidationChangeRef = useRef(onValidationChange);
    const onPartDataMatchRef = useRef(onPartDataMatch);

    useEffect(() => {
        onChangeRef.current = onChange;
        onValidationChangeRef.current = onValidationChange;
        onPartDataMatchRef.current = onPartDataMatch;
    });

    useEffect(() => {
        if (!enabled) {
            setStatus(MASTER_PART_NO_VALIDATION_STATUS.IDLE);
            setMessage('');
            if (onValidationChangeRef.current) {
                onValidationChangeRef.current(MASTER_PART_NO_VALIDATION_STATUS.VALID);
            }
            if (onPartDataMatchRef.current) onPartDataMatchRef.current(null);
            return undefined;
        }

        const partNo = normalizeMasterPartNo(value);
        if (!partNo) {
            setStatus(MASTER_PART_NO_VALIDATION_STATUS.IDLE);
            setMessage('');
            if (onValidationChangeRef.current) {
                onValidationChangeRef.current(MASTER_PART_NO_VALIDATION_STATUS.IDLE);
            }
            if (onPartDataMatchRef.current) onPartDataMatchRef.current(null);
            return undefined;
        }

        const currentValidationId = validationId.current + 1;
        validationId.current = currentValidationId;
        setStatus(MASTER_PART_NO_VALIDATION_STATUS.CHECKING);
        setMessage('');
        if (onValidationChangeRef.current) {
            onValidationChangeRef.current(MASTER_PART_NO_VALIDATION_STATUS.CHECKING);
        }

        const timeoutId = setTimeout(() => {
            checkFullPartNoExists({
                apiUrl: config.apiUrl,
                company: company || getCurrentUserCompany(),
                partNo,
            })
                .then((result) => {
                    if (validationId.current !== currentValidationId) return;
                    const exists = result && result.exists;
                    const isObsolete = result && result.isObsolete;
                    const canonicalPartNo = result && result.fullPartNo ? result.fullPartNo : '';
                    const nextStatus = exists && !isObsolete
                        ? MASTER_PART_NO_VALIDATION_STATUS.VALID
                        : MASTER_PART_NO_VALIDATION_STATUS.INVALID;
                    setStatus(nextStatus);
                    setMessage(exists
                        ? (isObsolete ? MASTER_PART_NO_OBSOLETE_MESSAGE : '')
                        : MASTER_PART_NO_NOT_FOUND_MESSAGE);
                    if (exists && canonicalPartNo && canonicalPartNo !== value && onChangeRef.current) {
                        onChangeRef.current(canonicalPartNo);
                    }
                    if (onPartDataMatchRef.current) {
                        onPartDataMatchRef.current(exists && !isObsolete ? result.part : null);
                    }
                    if (onValidationChangeRef.current) {
                        onValidationChangeRef.current(nextStatus);
                    }
                })
                .catch(() => {
                    if (validationId.current !== currentValidationId) return;
                    setStatus(MASTER_PART_NO_VALIDATION_STATUS.ERROR);
                    setMessage(MASTER_PART_NO_VALIDATION_ERROR_MESSAGE);
                    if (onPartDataMatchRef.current) onPartDataMatchRef.current(null);
                    if (onValidationChangeRef.current) {
                        onValidationChangeRef.current(MASTER_PART_NO_VALIDATION_STATUS.ERROR);
                    }
                });
        }, 350);

        return () => clearTimeout(timeoutId);
    }, [value, enabled, company]);

    const showChecking = enabled && status === MASTER_PART_NO_VALIDATION_STATUS.CHECKING;
    const showMessage = enabled && message;
    const describedBy = showChecking || showMessage ? statusId.current : undefined;

    return (
        <React.Fragment>
            <FormControl
                {...formControlProps}
                type={formControlProps.type || 'text'}
                onChange={ev => onChange(ev.target.value)}
                value={value}
                aria-invalid={status === MASTER_PART_NO_VALIDATION_STATUS.INVALID || status === MASTER_PART_NO_VALIDATION_STATUS.ERROR}
                aria-describedby={describedBy}
            />
            {showChecking && (
                <div id={statusId.current} aria-live="polite" style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px' }}>
                    Checking Part No...
                </div>
            )}
            {showMessage && (
                <div id={statusId.current} aria-live="assertive" style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    {message}
                </div>
            )}
        </React.Fragment>
    );
}

export default MasterPartNoInput;
